import type { ReactElement } from 'react'
import type { SalesOrder, SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import { OrderDetailPanel } from '@/components/orders/OrderDetailPanel'
import CharacterCount from '@/components/form/CharacterCount'
import { OrderAsideFormFooter } from '@/components/orders/OrderAside'
import {
  ORDER_ASIDE_FORM_SURFACE_CLASS,
  ORDER_ASIDE_RAISED_SURFACE_CLASS,
} from '@/components/orders/order-aside.styles'
import { Typography } from '@/components/ui/typography'
import { serverFieldErrors, useAppForm } from '@/lib/form'
import { ORDER_FIELD_MAX_LENGTHS, ORDER_FIELD_NATIVE_MAX_LENGTHS } from '@/lib/order-field-limits'
import { useUpdateSalesOrderMutation } from '@/lib/services/sales-orders/sales-orders.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { MessageCircleHeart } from 'lucide-react'
import { toast } from 'sonner'

function EditCardMessageAndSignatureForm({
  initialData,
  closeAside,
  salesOrderId,
  cardMessage,
  signature,
}: {
  initialData?: Partial<Pick<SalesOrder, 'id'>>
  closeAside?: () => void
  salesOrderId: SalesOrderIdInput
  cardMessage?: SalesOrder['card_message']
  signature?: SalesOrder['signature']
}): ReactElement {
  const { mutateAsync: updateSalesOrder, isPending } = useUpdateSalesOrderMutation()

  const form = useAppForm({
    defaultValues: {
      card_message: cardMessage || '',
      signature: signature || '',
    },
    validators: {
      onSubmitAsync: async ({ value }) => {
        try {
          await updateSalesOrder({
            id: salesOrderId,
            card_message: value.card_message,
            signature: value.signature,
          })
          toast.success('Card message and signature updated successfully')
          closeAside?.()
          return undefined
        } catch (error) {
          toast.error('Card message and signature update failed', {
            description: getErrorMessage(error),
          })

          // Return validation errors to display them on the form fields
          return serverFieldErrors(error)
        }
      },
    },
  })

  return (
    <form.AppForm>
      <form.Form className="flex min-h-full flex-col pt-5 pl-0 sm:pt-6 sm:pl-1">
        <OrderDetailPanel
          title="Card Message"
          subtitle="Update what should be written on the enclosure card."
          icon={MessageCircleHeart}
          tone="mint"
          className="shadow-none"
        >
          <form.Subscribe selector={(state) => state.values}>
            {(values) => (
              <div className="space-y-4 rounded-xl border border-primary/30 bg-accentSecondary/45 p-3.5 dark:bg-accentSecondary/30">
                <Typography variant="eyebrow" tone="accent" className="block">
                  Live preview
                </Typography>
                <div
                  className={cn(
                    ORDER_ASIDE_RAISED_SURFACE_CLASS,
                    'px-4 py-3 dark:border-white/10 dark:bg-white/[0.045] dark:shadow-none'
                  )}
                >
                  <span className="block text-[1.02rem] leading-8 text-foreground sm:text-[1.08rem]">
                    {values.card_message || 'No card message has been added yet.'}
                  </span>
                  {values.signature ? (
                    <Typography
                      variant="body"
                      italic
                      className="mt-3 block border-t border-border/70 pt-2.5"
                    >
                      {`— ${values.signature}`}
                    </Typography>
                  ) : null}
                </div>
              </div>
            )}
          </form.Subscribe>

          <div className={ORDER_ASIDE_FORM_SURFACE_CLASS}>
            <div className="grid gap-y-5">
              <div className="grid gap-y-4">
                <form.AppField name="card_message">
                  {(field) => {
                    return (
                      <div className="space-y-1">
                        <field.TextareaInput
                          id={`${initialData?.id}-card_message`}
                          label="Card message"
                          placeholder="What should be written on the enclosure card?"
                          maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.card_message}
                          characterMaxLength={ORDER_FIELD_MAX_LENGTHS.card_message}
                          rows={5}
                        />
                        <CharacterCount
                          value={field.state.value}
                          maxLength={ORDER_FIELD_MAX_LENGTHS.card_message}
                        />
                      </div>
                    )
                  }}
                </form.AppField>

                <form.AppField name="signature">
                  {(field) => (
                    <field.TextInput
                      id={`${initialData?.id}-signature`}
                      label="Signature"
                      placeholder="Who is this card from?"
                      maxLength={ORDER_FIELD_NATIVE_MAX_LENGTHS.signature}
                      characterMaxLength={ORDER_FIELD_MAX_LENGTHS.signature}
                    />
                  )}
                </form.AppField>
              </div>
            </div>
          </div>
        </OrderDetailPanel>
        <OrderAsideFormFooter
          form={form}
          mutation={{ isPending }}
          saveLabel="Save Card Message"
          onCancel={closeAside}
        />
      </form.Form>
    </form.AppForm>
  )
}

export default EditCardMessageAndSignatureForm
